import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BriefingRequestModal from '../../components/BriefingRequestModal';
import { strategy } from '../../data/strategy';
import { MotionReveal } from '../../components/strategy/Motion';
import { RoadmapArea } from '../../components/strategy/Charts';
import AfricaMap from '../../components/strategy/AfricaMap';

const Roadmap = () => {
  const [audience, setAudience] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App font-sans bg-white">
      <Header onPartnerClick={() => setAudience('capital')} />

      <section className="pt-32 pb-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="roadmap-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">20-Year Roadmap · 2026 – 2046</div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Five phases. Disciplined execution.
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mt-6">
            From foundation to continental scale. Each phase has explicit KPIs, governance milestones and capital benchmarks.
          </p>
        </div>
      </section>

      <section className="py-12 px-6 bg-[#0a1729] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#7fd6c9] font-bold mb-3">Cumulative Deployment Curve</div>
          <h2 className="font-serif text-2xl md:text-3xl font-bold mb-6 max-w-3xl">From $5B foundation to a $500B platform — compounding through five phases.</h2>
          <MotionReveal>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
              <RoadmapArea
                data={[
                  { phase: 'Phase I',  value: 5 },
                  { phase: 'Phase II', value: 30 },
                  { phase: 'Phase III',value: 125 },
                  { phase: 'Phase IV', value: 275 },
                  { phase: 'Phase V',  value: 500 },
                ]}
                theme="dark"
                height={320}
              />
            </div>
          </MotionReveal>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto space-y-10">
          {strategy.phases.map((p, idx) => (
            <MotionReveal key={p.label} delay={idx * 60}>
              <div className="grid md:grid-cols-12 gap-6 pb-10 border-b border-gray-100 last:border-0">
              <div className="md:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#2f9e8e] mb-2">{p.label} · {p.years}</div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight mb-4">{p.title}</h2>
                <div className="text-sm text-gray-500 mb-4">Phase {idx + 1} of 5</div>
              </div>
              <div className="md:col-span-4">
                <h3 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3">Objectives</h3>
                <ul className="space-y-2">
                  {p.objectives.map((o) => (
                    <li key={o} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2f9e8e] flex-shrink-0 mt-1.5" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-4">
                <h3 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3">Targets</h3>
                <div className="space-y-1.5">
                  {p.targets.map((t) => (
                    <div key={t.k} className="flex items-center justify-between py-2 px-3 bg-[#f6f9fb] rounded">
                      <span className="text-xs text-gray-600">{t.k}</span>
                      <span className="text-sm font-bold text-[#1a365d]">{t.v}</span>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </MotionReveal>
          ))}
        </div>
      </section>

      {/* Geographic phases — with map */}
      <section className="py-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">Geographic Strategy</div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold mb-10 max-w-3xl">
            Anchor markets, regional scaling, then continental integration.
          </h2>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <MotionReveal className="lg:col-span-5">
              <AfricaMap />
            </MotionReveal>
            <div className="lg:col-span-7 space-y-4">
              {Object.values(strategy.geographies).map((g, idx) => {
                const tierColors = ['#7fd6c9', '#c19a3c', '#94a3b8'];
                return (
                  <MotionReveal key={g.label} delay={idx * 100}>
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 hover:border-white/30 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: tierColors[idx], boxShadow: `0 0 16px ${tierColors[idx]}` }} />
                        <span className="text-xs font-bold uppercase tracking-wider text-white">{g.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-5">
                        {g.countries.map((c) => (
                          <span key={c} className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 text-white rounded-full font-medium">{c}</span>
                        ))}
                      </div>
                    </div>
                  </MotionReveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Engage with the platform.</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => setAudience('capital')} className="bg-[#2f9e8e] hover:bg-[#268578] px-6 py-3 rounded-lg font-semibold" data-testid="roadmap-cta-capital">Capital Partners <ArrowRight className="inline w-4 h-4 ml-1" /></button>
            <button onClick={() => setAudience('sovereign')} className="bg-[#c19a3c] hover:bg-[#a8842f] px-6 py-3 rounded-lg font-semibold">Sovereign <ArrowRight className="inline w-4 h-4 ml-1" /></button>
            <button onClick={() => setAudience('corporate')} className="bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-lg font-semibold">U.S. Corporate <ArrowRight className="inline w-4 h-4 ml-1" /></button>
          </div>
        </div>
      </section>

      <Footer />
      {audience && <BriefingRequestModal isOpen={!!audience} onClose={() => setAudience(null)} audience={audience} />}
    </div>
  );
};

export default Roadmap;
